import { debounce } from "lodash";
import JitsiMeetJS, { JitsiConferenceEvents, JitsiTrackErrors } from "../../features/base/lib-jitsi-meet";
import { VIDEO_TYPE } from "../../features/base/media";
import { getLocalParticipant, getLocalParticipantId } from "../../features/base/participants";
import { setScreenVideoShareState } from "../../features/screen-share";
import { setVideoLayoutProperty } from "../../features/video-layout-mode";

class ScreenShare {
    constructor() {
        this.handler = null;

        this.desktopStream = null;
        this.connection = null;
        this.conference = null;
        this.shareUserID = null;

        this.videoSwitchInProgress = false;
        
        this._unToggleScreenSharing = this._unToggleScreenSharing.bind(this);
    }
    
    init(handler) {
        this.handler = handler;
    }

    isScreenSharing() {
        return this.shareUserID;
    }

    setDesktopStream = (stream) => {
        this.desktopStream = stream;
    }

    toggleScreenSharing(toggle) {
        console.log(`toggleScreenSharing: ${toggle}`);
        
        if (this.videoSwitchInProgress) {
            return Promise.reject('Switch in progress.');
        }
        if (!JitsiMeetJS.isDesktopSharingEnabled()) {
            return Promise.reject('Cannot toggle screen sharing: not supported.');
        }

        if (toggle) {
            try {
                return this.switchToScreenSharing();                
            } catch (err) {
                console.log('Failed to switch to screensharing', err, toggle);
                return;
            }
        }

        return this._unToggleScreenSharing();
    }   

    switchToScreenSharing() {
        this.videoSwitchInProgress = true;
        APP.UI.showLoading(true);

        this.handler.createDesktopTrack({
            unToggleScreenSharing: this._unToggleScreenSharing
        }).then(streams => {
            if (streams) {
                this.setDesktopStream(streams);
                this.screenConnect();
            }
        }).catch(error => {
            this.videoSwitchInProgress = false;
            APP.UI.showLoading(false);

            if (this._untoggleScreenSharing) this._untoggleScreenSharing();
            _handleScreenSharingError(error);

            return Promise.reject(error);
        });
    }

    screenConnect() {
        const connection = new JitsiMeetJS.JitsiConnection(null, null, connectionConfig);

        connection.addEventListener(
            JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, () => {
                this.videoSwitchInProgress = false;
                APP.UI.showLoading(false);
                this.connection = connection;

                const roomCode = APP.mateManagement.getRoom('code');
                const conference = this.connection.initJitsiConference(roomCode, {
                    openBridgeChannel: 'websocket'
                });

                this.desktopStream && this.desktopStream.map(track => {
                    // track & screen share id update 
                    this.shareUserID = conference.myUserId();
            
                    conference.addTrack(track);                    
                });

                conference.on(JitsiConferenceEvents.CONFERENCE_LEFT, (...args) => {
                    this._unToggleScreenSharing();
                });

                conference.on(JitsiConferenceEvents.TRACK_ADDED, track => {
                    if (track && track.isLocal()) {
                        if (track.videoType === VIDEO_TYPE.DESKTOP) {
                            const track_id = track.getTrackId();
                            const member_uuid = getLocalParticipant(APP.store.getState()).member_uuid;
                            
                            APP.store.dispatch(setScreenVideoShareState({ member_uuid: member_uuid, track_id: this.shareUserID }));
                        }
                    }
                });

                conference.join();
    
                this.conference = conference;
        });

        connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, () => {
            this.connection = null;
            this.disposeVideoStream();
            this.appContext.mateConnection.disableScreenButton('화면 공유 네트워크 오류가 발생하였습니다. 다시 한번 시도해주세요.');
        });

        connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, () => {
            this.connection = null;
            this.disposeVideoStream();
        });

        connection.connect();
    }

    _unToggleScreenSharing() {
        this.disposeVideoStream();
        
        this.videoSwitchInProgress = false;
        this.shareUserID = null;

        if (this.conference) {
            this.conference.leave();
            this.conference = null;
        }

        if (this.connection) {
            this.connection.disconnect();
            this.connection = null;
        }

        APP.UI.showLoading(false);
        APP.store.dispatch(setScreenVideoShareState(false));
        APP.store.dispatch(setVideoLayoutProperty({ screenModal: false }));
    }

    disposeVideoStream() {
        this.videoSwitchInProgress = false;
        
        this.desktopStream && this.desktopStream.map(track => {
            !track.disposed && track.dispose();    
        });
        
        this.desktopStream = null;
    }
}

const connectionConfig = {
    serviceUrl: `wss://${location.host}/xmpp-websocket`,
    hosts: {
        domain: "mate.net",
        muc: "muc.mate.net",
    }
};

function _handleScreenSharingError(error) {
    let descriptionKey;
    let titleKey;
    APP.store.dispatch(setScreenVideoShareState(false));
    APP.store.dispatch(setVideoLayoutProperty({ screenModal: false }));
    
    if (error.name === JitsiTrackErrors.SCREENSHARING_USER_CANCELED) {
        descriptionKey = 'dialog.screenSharingCancel';
        titleKey = 'dialog.screenSharingCancelError';
    } else if (error.name === JitsiTrackErrors.PERMISSION_DENIED) {
        descriptionKey = 'dialog.screenSharingPermissionDeniedError';
        titleKey = 'dialog.screenSharingFailedTitle';
    } else if (error.name === JitsiTrackErrors.CONSTRAINT_FAILED) {
        descriptionKey = 'dialog.cameraConstraintFailedError';
        titleKey = 'deviceError.cameraError';
    } else if (error.name === JitsiTrackErrors.SCREENSHARING_GENERIC_ERROR) {
        descriptionKey = 'dialog.screenSharingFailed';
        titleKey = 'dialog.screenSharingFailedTitle';
    } 

    APP.UI.messageHandler.showError({
        descriptionKey,
        titleKey
    });
} 

const screenShare = new ScreenShare();
export default screenShare;